// Site content for Daniel Podbrezsky portfolio - based on CV, LinkedIn and Upwork profiles

export const personalInfo = {
  name: "Daniel Podbrezsky",
  role: "Mobile & Front-End Developer",
  stack: "Flutter · React · Next.js · TypeScript",
  tagline: "I build and ship products end to end — from React and Next.js interfaces to cross-platform mobile apps on iOS and Android",
  quote: "Real-time mobile apps that thousands of people actually use. Front-end systems that hold up under real traffic. Ideas that went from a sketch to something live in the App Store.",
  email: "daniel@podbrezsky.com",
  phone: "+371 225 323 04",
  website: "daniel.podbrezsky.com",
  location: "Riga, Latvia",
  linkedin: "https://www.linkedin.com/in/daniel-podbrezsky/",
  github: "https://github.com/wariok-gm",
  calendly: "https://calendly.com/daniel-podbrezsky",
  summary: "My deepest mobile experience is in Flutter, where I owned the client of a real-time messaging app that scaled to 50,000 concurrent users and took it through App Store and Play Store launch. I'm comfortable across the stack — REST APIs, third-party integrations, and automation — and I care about how features connect into a coherent product. I do my best work in fast-moving teams where I can help shape what gets built.",
  whyWorkWithMe: [
    {
      title: "Shipped, Not Just Built",
      description: "Apps taken all the way through App Store and Play Store launch",
      icon: "Rocket"
    },
    {
      title: "Proven At Scale",
      description: "50,000 concurrent users at sub-50ms message latency",
      icon: "Zap"
    },
    {
      title: "Clear Communication",
      description: "Daily updates and a working demo in the first week",
      icon: "MessageSquare"
    },
    {
      title: "Full Ownership",
      description: "Features owned end to end, from empty screen to production",
      icon: "Award"
    }
  ]
};

export const expertise = [
  {
    id: 1,
    title: "Cross-Platform Mobile Apps",
    description: "One Flutter or React Native codebase running on iOS and Android — around 95% shared code, deployed to the App Store and Play Store.",
    icon: "Smartphone"
  },
  {
    id: 2,
    title: "Web & Front-End",
    description: "React, Next.js and TypeScript interfaces built with Tailwind CSS. Responsive, fast-loading, and built to be maintained.",
    icon: "Code"
  },
  {
    id: 3,
    title: "Real-Time Features",
    description: "Chat, voice and video calls, live feeds and offline-first sync using WebSockets, LiveKit, Agora and local SQLite.",
    icon: "Radio"
  },
  {
    id: 4,
    title: "APIs & Integrations",
    description: "REST API design plus the third-party plumbing that makes a product real — Stripe, Firebase, Supabase and Mapbox.",
    icon: "Plug"
  },
  {
    id: 5,
    title: "Web Applications",
    description: "Custom dashboards, booking and scheduling systems, and member portals with authentication over PostgreSQL.",
    icon: "LayoutDashboard"
  },
  {
    id: 6,
    title: "Business Automation",
    description: "62+ automation flows across Airtable, Make.com and Slack, saving an estimated 253+ hours of manual work per month.",
    icon: "Workflow"
  }
];

export const projects = [
  {
    id: 1,
    title: "OnFire Messenger",
    description: "Cross-platform social and productivity app — messaging, social feeds, marketplace and wallet — that scaled to 50,000 concurrent users. I owned the Flutter client from the first commit.",
    tech: "Flutter, Dart, Supabase, PostgreSQL, Stripe, LiveKit, Agora",
    image: "https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=800&h=600&fit=crop",
    highlights: [
      "50,000 concurrent users at sub-50ms latency",
      "~95% shared code across iOS and Android",
      "Offline mode with local-first SQLite sync"
    ]
  },
  {
    id: 2,
    title: "Q Music Vendor Platform",
    description: "Vendor management and venue booking platform that took the business from a regional Chicago and Detroit operation to a US national brand, and helped secure a FIFA 2026 vendor partnership.",
    tech: "Airtable, Softr, Make.com, Slack API, iOS Deployment",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
    highlights: [
      "1,000+ vendors across 36+ locations",
      "220+ shifts coordinated per weekend",
      "62+ flows saving ~253 hours per month"
    ],
    links: [
      { label: "qmusic.co", url: "https://qmusic.co" },
      { label: "App Store", url: "https://apps.apple.com/app/qmusic-venue/id6755474782" }
    ]
  },
  {
    id: 3,
    title: "Welfare-Check & Dispatch Platform",
    description: "The 24/7 system behind UK emergency-services welfare checks: 6,000 workers called roughly every 25 minutes to confirm they were safe, with managers alerted on any missed check-in.",
    tech: "PostgreSQL, SQL, Airtable, Make.com, Webhooks",
    image: "https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&h=600&fit=crop",
    highlights: [
      "~50-table PostgreSQL schema",
      "Zero-downtime Airtable to Postgres migration",
      "6,000 workers under 60 managers"
    ]
  },
  {
    id: 4,
    title: "TalentMatch — Mock App",
    description: "A mock-up casting app I built to get properly hands-on with React Native. Talent discovery, profiles and messaging run against a simulated API — local data with realistic latency and failure rates — rather than a live backend. A Flutter version is in progress.",
    tech: "React Native, Expo, TypeScript, Redux Toolkit",
    image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop",
    highlights: [
      "Personal demo project, open source on GitHub",
      "Simulated API drives real loading and error states",
      "Typed end to end, with a Jest suite and CI"
    ],
    links: [
      { label: "View on GitHub", url: "https://github.com/wariok-gm/TalentMatch" }
    ]
  }
];

export const experience = [
  {
    id: 1,
    company: "OnFire Messenger Inc.",
    role: "Mobile Developer",
    period: "Mar 2025 - Jan 2026",
    location: "New York, United States · Remote",
    achievements: [
      "Joined at the start of the project and scaffolded the Flutter app from scratch, then built the first versions of the core screens: feed, chat, chat list, video and audio calls, and camera",
      "Built out calendar, explore, health tracking, notes, awards and today views, plus the complete task creation flow — naming, complexity, tagging, delegation, scheduling and goal assignment",
      "Rebuilt the chat experience into modular components: message input, audio recording, attachment picker, message bubbles, scheduling and a quick actions menu",
      "Built the stories feature, the profile screen and a QR-code based contact sharing flow",
      "Integrated Stripe, Firebase, LiveKit, Agora and Mapbox into a single production mobile client",
      "Implemented local-first sync with SQLite (Drift ORM) against a PostgreSQL/Supabase backend using Provider for state management",
      "Took the app from iOS-only to full Android support and adapted the client for the backend's zero-downtime migration from Django REST to PostgREST/Supabase behind feature flags"
    ],
    skills: ["Flutter/Dart", "PostgreSQL", "Supabase", "REST APIs", "WebSockets", "Stripe", "LiveKit"]
  },
  {
    id: 2,
    company: "Q Music",
    role: "Platform Developer",
    period: "Dec 2024 - Mar 2025",
    location: "Austin, United States · Remote",
    achievements: [
      "Built a 6-phase Airtable and Softr portal for vendor onboarding, shift scheduling and location management, covering 1,000+ vendors across 36+ locations and 220+ shifts per weekend",
      "Created 62+ automation flows across Airtable, Make.com and Slack, saving an estimated 253+ hours of manual coordination per month",
      "Handled iOS App Store submission for the companion venue-booking app — icon and preview assets, Xcode launch configuration and store listing verification",
      "The platform helped the company land a FIFA 2026 vendor partnership and expand into New York, Miami, Toronto and Mexico"
    ],
    skills: ["Airtable", "Softr", "Make.com", "Slack API", "App Store"],
    links: [
      { label: "qmusic.co", url: "https://qmusic.co" },
      { label: "App Store", url: "https://apps.apple.com/app/qmusic-venue/id6755474782" }
    ]
  },
  {
    id: 3,
    company: "Lanos Logic LTD",
    role: "Backend & Database Developer",
    period: "Jul 2024 - Mar 2025",
    location: "Bracknell, United Kingdom · On-site",
    achievements: [
      "Worked on a 5-person team building the platform behind a 24/7 welfare-check and dispatch system for UK emergency services, covering 6,000 workers under 60 managers",
      "Designed and maintained the PostgreSQL schema behind the platform — around 50 tables covering calls, messages, emergencies and managers",
      "Migrated the data layer from Airtable to PostgreSQL, running both databases and duplicating automations in parallel until the cutover was fully verified, so no welfare check was ever missed",
      "Wrote and optimised SQL across the schema: joins, subqueries, CTEs, window functions and aggregate queries",
      "Built and maintained the automation layer feeding the calling and escalation system"
    ],
    skills: ["PostgreSQL", "SQL", "Schema Design", "Airtable", "Make.com", "Webhooks"]
  }
];

export const technicalSkills = {
  mobile: ["Flutter", "Dart", "React Native", "App Store & Play Store"],
  frontend: ["React", "Next.js", "TypeScript", "JavaScript (ES6+)", "Tailwind CSS", "HTML & CSS"],
  backend: ["Python", "Django", "FastAPI", "Node.js", "REST API design", "WebSockets"],
  databases: ["PostgreSQL", "Supabase", "SQLite"],
  tools: ["Stripe", "Firebase", "LiveKit", "Agora", "Mapbox", "Git"]
};

export const strengths = [
  "Ownership & initiative",
  "Clear communication",
  "Problem-solving",
  "Team collaboration",
  "Adaptability"
];
